import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Users } from "lucide-react";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import ApiService from "../../services/api-service";
import type { FollowListUser } from "../../types/social";

const PAGE_SIZE = 8;

type FollowListType = "followers" | "following";

interface FollowListHoverCardProps {
  userId: string | number;
  type: FollowListType;
  children: React.ReactNode;
}

interface FollowListResponse {
  users?: FollowListUser[];
  hasMore?: boolean;
}

function fetchFollowList(userId: string | number, type: FollowListType, offset: number): Promise<FollowListResponse> {
  return type === "followers"
    ? ApiService.getFollowers(userId, PAGE_SIZE, offset)
    : ApiService.getFollowing(userId, PAGE_SIZE, offset);
}

function initials(name: string) {
  return name.slice(0, 2).toUpperCase() || "?";
}

// A mouse that can hover (desktop). Phones, tablets and the app use taps.
const canHover = () =>
  typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

function FollowListContent({
  userId,
  type,
  onNavigate,
}: {
  userId: string | number;
  type: FollowListType;
  onNavigate?: () => void;
}) {
  const [users, setUsers] = useState<FollowListUser[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    fetchFollowList(userId, type, 0)
      .then((response) => {
        if (cancelled) return;
        setUsers(response?.users || []);
        setHasMore(Boolean(response?.hasMore));
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, type]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    fetchFollowList(userId, type, users.length)
      .then((response) => {
        setUsers((prev) => [...prev, ...(response?.users || [])]);
        setHasMore(Boolean(response?.hasMore));
      })
      .catch(() => {})
      .finally(() => setIsLoadingMore(false));
  };

  const emptyLabel = type === "followers" ? "No followers yet." : "Not following anyone yet.";

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2.5">
            <Skeleton className="h-9 w-9 rounded-full shrink-0" />
            <Skeleton className="h-3.5 w-28" />
          </div>
        ))}
      </div>
    );
  }

  if (hasError) {
    return <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">Couldn't load this list.</p>;
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Users className="h-6 w-6 text-slate-300 dark:text-slate-600" />
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="max-h-72 space-y-1 overflow-y-auto themed-scrollbar">
      {users.map((user) => {
        const name = user.first_name || user.username;
        return (
          <Link
            key={user.id}
            to={`/user/${user.username}/${user.id}`}
            onClick={onNavigate}
            className="flex items-center gap-2.5 rounded-md px-1.5 py-2 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Avatar className="h-9 w-9 shrink-0">
              {user.profile_pic ? <AvatarImage src={user.profile_pic} alt={name} className="object-cover" /> : null}
              <AvatarFallback className="bg-slate-200 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{name}</span>
          </Link>
        );
      })}

      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLoadMore}
          disabled={isLoadingMore}
          className="mt-1 w-full text-slate-600 dark:text-slate-300"
        >
          {isLoadingMore && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Load more
        </Button>
      )}
    </div>
  );
}

// The followers / following count on a profile. With a mouse, hovering shows
// the list; on touch screens (phones, tablets, the app) tapping opens it.
export function FollowListHoverCard({ userId, type, children }: FollowListHoverCardProps) {
  const title = type === "followers" ? "Followers" : "Following";
  const [hoverable] = useState(canHover);
  const [open, setOpen] = useState(false);

  const trigger = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={`Show ${title.toLowerCase()}`}
      className="cursor-pointer rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
    >
      {children}
    </button>
  );
  const heading = (
    <h4 className="mb-2 px-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
      {title}
    </h4>
  );

  if (hoverable) {
    return (
      <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
        <HoverCardContent className="w-72 p-3">
          {heading}
          <FollowListContent userId={userId} type={type} />
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] p-3">
        {heading}
        {open && <FollowListContent userId={userId} type={type} onNavigate={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
  );
}
